import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import styles from "../styles/Legal.module.css";
import { buildCanonicalUrl } from "../utils/basePath";

const RefundPolicy: React.FC = () => {
  return (
    <>
      <SEO
        title="Refund Policy - Rythm"
        description="Refund Policy for Rythm - Understand eligibility, timelines, and how to request a refund for purchases or subscriptions."
        canonicalUrl={buildCanonicalUrl("/refund-policy")}
        keywords="rythm refund policy, refunds, subscription refunds, billing support"
        ogImage="https://rythm.fm/assets/images/terms-og.png"
        ogImageAlt="Rythm Refund Policy - Terms for refunds and cancellations"
        schemaType="WebPage"
      />
      <Header />
      <div className={styles.legalContainer}>
        <h1>Refund Policy</h1>
        <p className={styles.lastUpdated}>Last Updated: October 29, 2025</p>
        <div className={styles.legalContent}>
          <p>
            This Refund Policy ("Policy") outlines the terms and conditions under
            which refunds may be issued for purchases made through Rythm ("we,"
            "us," or "our"). By making a purchase or maintaining a subscription,
            you agree to be bound by this Policy in addition to our{" "}
            <Link to="/terms-of-service">Terms of Service</Link>.
          </p>

          <section>
            <h2>1. Eligibility for Refunds</h2>
            <p>
              Refunds may be requested within seven (7) days of the purchase date.
              The purchase date refers to the date on which payment is processed,
              not the start date of any trial period.
            </p>
            <p>Refund requests submitted after seven (7) days from the purchase date are not eligible.</p>
            <p>Refunds outside this window will only be provided where required by applicable law.</p>
          </section>

          <section>
            <h2>2. How to Request a Refund</h2>
            <p>Refund requests must be submitted through one of the following channels:</p>
            <ul>
              <li>
                Email: <a href="mailto:support@rythm.fm">support@rythm.fm</a>
              </li>
              <li>
                Discord Support Server:{" "}
                <a href="https://discord.com/invite/rythm" target="_blank" rel="noopener noreferrer">
                  https://discord.com/invite/rythm
                </a>
              </li>
            </ul>
            <p>
              When requesting a refund, you must contact us using the same email address used to make the purchase or the
              same Discord account used to sign up for Rythm. This ensures we can verify your ownership of the purchase
              and process your request appropriately.
            </p>
          </section>

          <section>
            <h2>3. Discord Store Purchases</h2>
            <p>
              Premium subscriptions or other purchases made through the Discord Store are processed and managed by
              Discord, not by Rythm. We do not have the ability to issue or process refunds for these transactions.
            </p>
            <p>
              Users seeking a refund for a Discord Store purchase must contact Discord directly by submitting a support
              ticket at{" "}
              <a href="https://dis.gd/billing" target="_blank" rel="noopener noreferrer">
                https://dis.gd/billing
              </a>
              .
            </p>
          </section>

          <section>
            <h2>4. No Refunds or Credits for Partial Subscription Periods</h2>
            <p>
              In accordance with our Terms of Service, we do not provide refunds or credits for any partial subscription
              periods. This includes cancellations made before the end of a billing cycle or unused portions of a
              subscription.
            </p>
          </section>

          <section>
            <h2>5. Changes to This Policy</h2>
            <p>
              We may update or modify this Policy from time to time. The "Last Updated" date at the top of this page
              indicates when the most recent changes were made. Continued use of our services following any changes
              constitutes acceptance of the revised Policy.
            </p>
          </section>

          <section>
            <h2>6. Contact</h2>
            <p>Questions or concerns regarding this Policy may be directed to:</p>
            <ul>
              <li>
                Email: <a href="mailto:support@rythm.fm">support@rythm.fm</a>
              </li>
              <li>
                Discord Support Server:{" "}
                <a href="https://discord.com/invite/rythm" target="_blank" rel="noopener noreferrer">
                  https://discord.com/invite/rythm
                </a>
              </li>
            </ul>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default RefundPolicy;
